import { useEffect, useState } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useEntitlement } from "@/context/EntitlementContext";

export default function PaymentSuccess() {
  const loc = useLocation();
  const nav = useNavigate();
  const { refresh } = useEntitlement();
  const params = new URLSearchParams(loc.search);
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | timeout | error
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancel = false;
    let n = 0;
    const tick = async () => {
      if (cancel) return;
      n += 1;
      setAttempts(n);
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); refresh(); return; }
        if (data.payment_status === "expired" || data.payment_status === "failed") { setStatus("error"); return; }
      } catch (e) { console.warn("Payment status poll failed; will retry.", e); /* keep polling */ }
      if (n >= 15) { setStatus("timeout"); return; }
      setTimeout(tick, 2000);
    };
    tick();
    return () => { cancel = true; };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen bg-[#FDFDFB] flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center">
        <Link to="/" className="inline-flex items-center gap-2 mb-8">
          <Logo />
        </Link>
        {status === "polling" && (
          <div data-testid="payment-polling">
            <Loader2 className="w-8 h-8 mx-auto animate-spin text-[#2D4A3E]" />
            <h1 className="font-display text-2xl font-bold tracking-tight mt-6">Confirming your payment…</h1>
            <p className="text-sm text-[#5C6661] mt-2">Checking with Stripe (attempt {attempts})</p>
          </div>
        )}
        {status === "paid" && (
          <div data-testid="payment-success">
            <CheckCircle2 className="w-10 h-10 mx-auto text-[#2E7D32]" />
            <h1 className="font-display text-3xl font-bold tracking-tight mt-6">You&apos;re on JobKnocker Pro.</h1>
            <p className="text-sm text-[#5C6661] mt-2">All matched companies are unlocked — on web and in the mobile app.</p>
            <Button onClick={() => nav("/app")} className="mt-6 rounded-full bg-[#2D4A3E] hover:bg-[#3B5F50]">Open dashboard</Button>
          </div>
        )}
        {status === "timeout" && (
          <div data-testid="payment-timeout">
            <h1 className="font-display text-2xl font-bold tracking-tight">Still confirming…</h1>
            <p className="text-sm text-[#5C6661] mt-2">Payment might take a minute longer. Refresh or open the dashboard.</p>
            <Button onClick={() => nav("/app/settings")} className="mt-6 rounded-full bg-[#2D4A3E] hover:bg-[#3B5F50]">Go to settings</Button>
          </div>
        )}
        {status === "error" && (
          <div data-testid="payment-error">
            <h1 className="font-display text-2xl font-bold tracking-tight">Something didn&apos;t complete</h1>
            <p className="text-sm text-[#5C6661] mt-2">Your card wasn&apos;t charged. Try again from the pricing page.</p>
            <Link to="/pricing"><Button className="mt-6 rounded-full bg-[#2D4A3E] hover:bg-[#3B5F50]">Back to pricing</Button></Link>
          </div>
        )}
      </div>
    </div>
  );
}
